import type { ElementType } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { cn } from "@/lib/utils";

interface ImageFeatureCardProps {
  title: string;
  description: string;
  eyebrow?: string;
  image: string;
  imageAlt: string;
  href?: string;
  ctaLabel?: string;
  variant?: "light" | "dark";
  overlay?: boolean;
  className?: string;
}

const ImageFeatureCard = ({
  title,
  description,
  eyebrow,
  image,
  imageAlt,
  href,
  ctaLabel = "Explore",
  variant = "light",
  overlay = false,
  className,
}: ImageFeatureCardProps) => {
  const Wrapper = (href ? Link : "article") as ElementType;
  const wrapperProps = href ? { to: href } : {};
  const isDark = variant === "dark";

  return (
    <Wrapper
      {...(wrapperProps as object)}
      className={cn(
        "group block h-full overflow-hidden transition-all duration-500",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/70 focus-visible:ring-offset-2 focus-visible:ring-offset-background",
        overlay
          ? "relative min-h-[420px] border border-border"
          : "border border-border bg-card hover:border-accent/35 hover:shadow-[0_18px_50px_-24px_rgba(0,0,0,0.2)]",
        className
      )}
    >
      {overlay ? (
        <>
          <div className="absolute inset-0 overflow-hidden">
            <img
              src={image}
              alt={imageAlt}
              className="h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-[1.03]"
              loading="lazy"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-rich-black/96 via-rich-black/74 to-rich-black/28" />
            <div className="absolute inset-x-0 bottom-0 h-[72%] bg-gradient-to-t from-rich-black/92 via-rich-black/66 to-transparent" />
          </div>
          <div className="relative z-10 flex min-h-[420px] items-end p-5 md:p-6">
            <div className="w-full border border-cream/16 bg-rich-black/84 p-5 text-cream backdrop-blur-lg md:p-6">
              {eyebrow && (
                <p className="font-body text-[10px] uppercase tracking-[0.24em] text-accent mb-3">
                  {eyebrow}
                </p>
              )}
              <h3 className="font-headline text-2xl md:text-[1.9rem] leading-tight mb-3">
                {title}
              </h3>
              <p className="text-cream/92 text-base leading-[1.72]">{description}</p>
              {href && (
                <div className="pt-5">
                  <span className="inline-flex items-center gap-2 font-body text-sm uppercase tracking-[0.14em] text-accent transition-colors group-hover:text-accent/90">
                    {ctaLabel}
                    <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" />
                  </span>
                </div>
              )}
            </div>
          </div>
        </>
      ) : (
        <>
          <div className="overflow-hidden aspect-[16/11]">
            <img
              src={image}
              alt={imageAlt}
              className="h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-[1.03]"
              loading="lazy"
            />
          </div>
          <div
            className={cn(
              "flex h-full flex-col p-6 md:p-8",
              isDark ? "bg-secondary text-secondary-foreground" : "bg-card text-foreground"
            )}
          >
            {eyebrow && (
              <p className="font-body text-[10px] uppercase tracking-[0.24em] text-accent mb-3">
                {eyebrow}
              </p>
            )}
            <h3 className="font-headline text-2xl md:text-[1.9rem] leading-tight mb-3">
              {title}
            </h3>
            <p
              className={cn(
                "text-base leading-[1.75]",
                isDark ? "text-secondary-foreground/78" : "text-muted-foreground"
              )}
            >
              {description}
            </p>
            {href && (
              <div className="pt-5 mt-auto">
                <span className="inline-flex items-center gap-2 font-body text-sm uppercase tracking-[0.14em] text-accent transition-colors group-hover:text-accent/85">
                  {ctaLabel}
                  <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" />
                </span>
              </div>
            )}
          </div>
        </>
      )}
    </Wrapper>
  );
};

export default ImageFeatureCard;
